import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from "react-redux";
import jobOfferMediasReducer from "@/lib/redux/jobOfferMedias/jobOfferMediasSlice";
import executionFilesReducer from "@/lib/redux/executionFiles/executionFilesSlice";
import scoutSettingsReducer from "@/lib/redux/scoutSettings/scoutSettingsSlice";
import executionHistoriesReducer from "@/lib/redux/executionHistories/executionHistoriesSlice";
import mediaDisplaySettingsReducer from "@/lib/redux/mediaDisplaySettings/mediaDisplaySettingsSlice";
import billingProfileReducer from "@/lib/redux/billingProfile/billingProfileSlice";
import subscriptionReducer from "@/lib/redux/subscription/subscriptionSlice";
import paymentMethodReducer from "@/lib/redux/paymentMethod/paymentMethodSlice";
import scoutCountsReducer from "@/lib/redux/scoutCounts/scoutCountsSlice";
import dailySummaryReducer from "@/lib/redux/dailySummary/dailySummarySlice";
import invoicesReducer from "@/lib/redux/invoices/invoicesSlice";
import inquiriesReducer from "@/lib/redux/inquiries/inquiriesSlice";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  createMigrate,
  MigrationManifest,
  PersistedState,
} from "redux-persist";
import { storage } from "../storage";
const rootReducer = combineReducers({
  jobOfferMedias: jobOfferMediasReducer,
  executionFiles: executionFilesReducer,
  scoutSettings: scoutSettingsReducer,
  executionHistories: executionHistoriesReducer,
  mediaDisplaySettings: mediaDisplaySettingsReducer,
  billingProfile: billingProfileReducer,
  subscription: subscriptionReducer,
  paymentMethod: paymentMethodReducer,
  scoutCounts: scoutCountsReducer,
  dailySummary: dailySummaryReducer,
  invoices: invoicesReducer,
  inquiries: inquiriesReducer,
});

const migrations: MigrationManifest = {
  0: (state: PersistedState) => {
    if (
      state &&
      (Array.isArray(state) ||
        Object.keys(state).some((key) => !isNaN(Number(key))))
    ) {
      return {
        jobOfferMedias: jobOfferMediasReducer(undefined, { type: "INIT" }),
        executionFiles: executionFilesReducer(undefined, { type: "INIT" }),
        scoutSettings: scoutSettingsReducer(undefined, { type: "INIT" }),
        executionHistories: executionHistoriesReducer(undefined, {
          type: "INIT",
        }),
        mediaDisplaySettings: mediaDisplaySettingsReducer(undefined, {
          type: "INIT",
        }),
        _persist: state._persist || { version: -1, rehydrated: false },
      };
    }
    return state || { _persist: { version: -1, rehydrated: false } };
  },
};

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    "executionFiles",
    "jobOfferMedias",
    "scoutSettings",
    "mediaDisplaySettings",
    "executionHistories",
    "payment",
    "scoutCounts"
  ],
  version: 1,
  migrate: createMigrate(migrations, {
    debug: process.env.NODE_ENV !== "production",
  }),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        immutableCheck: false,
        serializableCheck: false,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const purgeStore = () => {
  persistor.purge();
};

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
